import { Dictionary } from 'lodash';
import moment from 'moment';
import { User } from 'api/models';
import { AllocationMedium } from 'api/resource-management/types';
import { ViewPresetId } from 'components/organisms/resource-management/scheduler-config/view-presets/constants';
import { getTimeOffArray } from 'utils/time-off-utils';
import {
  SchedulerEventType,
  UserAllocationSummaryEvent,
} from '../../../event-types';
import { AllocatedMinutes, UserAllocationSummary } from '../../../types';
import { getDetailedAllocationData } from '../../common';
import { idForUserAllocationSummaryEvent, resourceIdForUser } from '../../id';
import {
  getClassNameBasedOnAllocatedPercentage,
  getSchedulerEventProps,
  getSchedulerEventsClass,
} from '../common';

const getClassNameForUserSummaryEvent = (
  capacity: number,
  allocatedMinutes: AllocatedMinutes,
  isTimeOffPresent: boolean | undefined,
  softAllocationsEnabled?: boolean,
): string => {
  const { softAllocatedMinutes, allocatedPercentage } =
    getDetailedAllocationData(allocatedMinutes, capacity);

  const softEventClass =
    softAllocationsEnabled && softAllocatedMinutes > 0
      ? `${AllocationMedium.Soft}`.toLowerCase()
      : '';

  const timeOffClass = isTimeOffPresent ? 'time-off-user' : '';

  const classesToAdd = softEventClass.concat(timeOffClass);

  return getClassNameBasedOnAllocatedPercentage(
    allocatedPercentage,
    classesToAdd,
  );
};

export const userAllocationSummaryAsEvent = (
  userLookup: Dictionary<User>,
  allocationSummary: UserAllocationSummary,
  softAllocationsEnabled?: boolean,
): UserAllocationSummaryEvent => {
  const userTimeOff = userLookup[allocationSummary?.userId]?.userTimeOffDetails;

  const startDateMoment = moment(allocationSummary.startDate).startOf('day');
  const endDateMoment = moment(allocationSummary.endDate).endOf('day');

  const timeOffInRange = getTimeOffArray(
    startDateMoment,
    endDateMoment,
    userTimeOff,
  );

  const isTimeOffPresent = timeOffInRange && timeOffInRange.length > 0;

  return {
    type: SchedulerEventType.UserAllocationSummary,
    id: idForUserAllocationSummaryEvent(
      allocationSummary.userId,
      allocationSummary.startDate,
      allocationSummary.forViewPreset,
    ),
    resourceId: resourceIdForUser(allocationSummary.userId),
    startDate: startDateMoment.toDate(),
    endDate: endDateMoment.toDate(),
    capacity: allocationSummary.capacity,
    allocatedMinutes: allocationSummary.allocatedMinutes,
    forViewPreset: allocationSummary.forViewPreset,
    ...getSchedulerEventProps(SchedulerEventType.UserAllocationSummary, {
      cls: getSchedulerEventsClass(
        SchedulerEventType.UserAllocationSummary,
        getClassNameForUserSummaryEvent(
          allocationSummary.capacity,
          allocationSummary.allocatedMinutes,
          [ViewPresetId.Day, ViewPresetId.DayZoomed].includes(
            allocationSummary.forViewPreset,
          )
            ? false
            : isTimeOffPresent,
          softAllocationsEnabled,
        ),
      ),
    }),
  };
};
